/**
 * Symbole als Inline-SVG.
 *
 * Eine Symbolschrift oder ein Paket wäre für sechs Zeichen mehr Gewicht als
 * Nutzen -- und ein externer Verweis würde später die Offline-Hülle des
 * Service Workers unterlaufen.
 */

interface IconProps {
  size?: number
  className?: string
}

function Svg({
  size = 22,
  className,
  gefuellt,
  children,
}: IconProps & {
  /**
   * Gefüllt statt gestrichelt.
   *
   * **Aus dem Betrieb:** „die Symbole (nur an einen, an alle): etwas
   * prägnanter (farblich, fett) - das Schloßsymbol war prägnanter." Ein
   * Strichbild bei 13 Punkten ist ein paar Haare; eine gefüllte Fläche hat
   * dasselbe Gewicht wie das Emoji, das dort vorher stand.
   */
  gefuellt?: boolean
  children: React.ReactNode
}) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill={gefuellt === true ? 'currentColor' : 'none'}
      stroke={gefuellt === true ? 'none' : 'currentColor'}
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      aria-hidden="true"
      focusable="false"
    >
      {children}
    </svg>
  )
}

export const SmileyIcon = (props: IconProps) => (
  <Svg {...props}>
    <circle cx="12" cy="12" r="9" />
    <path d="M8.5 14.5a4.5 4.5 0 0 0 7 0" />
    <path d="M9 9.5h.01" />
    <path d="M15 9.5h.01" />
  </Svg>
)

/** Eine Filmklappe -- steht für ein Video, wo kein Standbild da ist. */
export const VideoIcon = (props: IconProps) => (
  <Svg {...props}>
    <rect x="2" y="5" width="14" height="14" rx="2" />
    <path d="m16 10 6-3v10l-6-3Z" />
  </Svg>
)

export const PhotoIcon = (props: IconProps) => (
  <Svg {...props}>
    <rect x="3" y="4" width="18" height="16" rx="2" />
    <circle cx="8.5" cy="9.5" r="1.5" />
    <path d="m21 16-5-5-6 6-3-3-4 4" />
  </Svg>
)

/** A collage (1.168.0): one big tile and two small ones, the default for three photos. */
export const CollageIcon = (props: IconProps) => (
  <Svg {...props}>
    <rect x="3" y="4" width="9" height="16" rx="2" />
    <rect x="14" y="4" width="7" height="7" rx="2" />
    <rect x="14" y="13" width="7" height="7" rx="2" />
  </Svg>
)

/**
 * Drei Balken verschiedener Länge — eine Abstimmung (#56).
 *
 * **Kein Häkchen und keine Wahlurne.** Ein Häkchen heißt hier schon
 * „gelesen", und eine Urne ist eine Wahl und keine Frage unter Freunden. Drei
 * ungleiche Balken sind das, was am Ende dasteht: ein Ergebnis.
 */
export const PollIcon = (props: IconProps) => (
  <Svg {...props}>
    <line x1="4" y1="7" x2="16" y2="7" />
    <line x1="4" y1="12" x2="20" y2="12" />
    <line x1="4" y1="17" x2="10" y2="17" />
  </Svg>
)

/**
 * Ein Kasten mit dem Wort darin -- der Weg zur GIF-Auswahl.
 *
 * Buchstaben und kein Sinnbild: „GIF" ist der Name der Sache und in jedem
 * Messenger derselbe. Ein gezeichnetes Symbol dafür müsste man erst lernen.
 */
export const GifIcon = (props: IconProps) => (
  <Svg {...props}>
    <rect x="2" y="5" width="20" height="14" rx="3" />
    <text
      x="12"
      y="15.5"
      textAnchor="middle"
      fontSize="8"
      fontWeight="700"
      fill="currentColor"
      stroke="none"
      fontFamily="system-ui, -apple-system, sans-serif"
    >
      GIF
    </text>
  </Svg>
)

/**
 * Eine Wellenform — der Weg zur Sprachnachricht, und was dabei herauskommt.
 *
 * **Hieß einmal `MikroIcon` und war ein Mikrofon.** Aus dem Betrieb: „das
 * Symbol sollten wir übrigens ersetzen. statt Mikrofon eine Wellenform." Ein
 * Mikrofon ist das *Gerät*; die Wellenform ist die **Sache** — und dieselbe
 * drei Stellen weiter: am Anhang ohne Standbild und in der Speicherübersicht
 * unter „Sprache" stand bisher ein Aufnahmegerät, wo eine Tonspur gemeint war.
 *
 * Fünf Balken um die Mitte, außen kurz, innen lang: bei 16 Punkten bleiben die
 * Abstände offen, bei 20 trägt es. Runde Enden hebt `Svg` an, sie ragen je
 * einen Punkt über die Werte hinaus — deshalb endet der höchste Balken bei 4
 * und 20 und nicht am Rand.
 */
export const WelleIcon = (props: IconProps) => (
  <Svg {...props}>
    <path d="M4 10v4" />
    <path d="M8 6v12" />
    <path d="M12 4v16" />
    <path d="M16 7v10" />
    <path d="M20 10v4" />
  </Svg>
)

/**
 * Eine Glocke — dieser Mensch bekommt Benachrichtigungen (#32).
 *
 * **Die Glocke und nicht das Auge**, obwohl aus dem Betrieb „z.B. ein Auge"
 * kam: das durchgestrichene Auge gibt es hier schon und steht für eine
 * zurückgezogene Reise. Zwei Bedeutungen für ein Bild sind dasselbe Problem
 * wie zwei Wörter für eine Sache.
 */
export const GlockeIcon = (props: IconProps) => (
  <Svg {...props}>
    <path d="M18 15v-4a6 6 0 1 0-12 0v4l-1.5 2.5h15L18 15Z" />
    <path d="M10 20a2 2 0 0 0 4 0" />
  </Svg>
)

/** Dieselbe Glocke, durchgestrichen — erreichbar, aber stummgeschaltet. */
export const GlockeAusIcon = (props: IconProps) => (
  <Svg {...props}>
    <path d="M18 15v-4a6 6 0 1 0-12 0v4l-1.5 2.5h15L18 15Z" />
    <path d="M10 20a2 2 0 0 0 4 0" />
    <path d="M4 3.5 20 20.5" />
  </Svg>
)

/**
 * Ein Mond — stummgeschaltet, und es kämen ohnehin keine Benachrichtigungen.
 *
 * Der Mond sagt „nicht stören" und nicht „abgeschaltet": wer kein Gerät
 * angemeldet hat, verpasst nichts, was er sonst bekommen hätte.
 */
export const MondIcon = (props: IconProps) => (
  <Svg {...props}>
    <path d="M20 14.5A8.5 8.5 0 0 1 9.5 4a8.5 8.5 0 1 0 10.5 10.5Z" />
  </Svg>
)

/** Ein Lupenglas — die Ortssuche auf der Karte. */
export const LupeIcon = (props: IconProps) => (
  <Svg {...props}>
    <circle cx="11" cy="11" r="7" />
    <path d="m20 20-3.5-3.5" />
  </Svg>
)

/** Ein Herz — auf einer Karte im Stapel zählt es die Reaktionen. */
export const HeartIcon = (props: IconProps) => (
  <Svg {...props}>
    <path d="M12 20s-7-4.4-7-9.2A4 4 0 0 1 12 8a4 4 0 0 1 7 2.8C19 15.6 12 20 12 20Z" />
  </Svg>
)

export const XIcon = (props: IconProps) => (
  <Svg {...props}>
    <path d="M18 6 6 18" />
    <path d="m6 6 12 12" />
  </Svg>
)

export const ChevronLeftIcon = (props: IconProps) => (
  <Svg {...props}>
    <path d="m15 18-6-6 6-6" />
  </Svg>
)

export const ChevronRightIcon = (props: IconProps) => (
  <Svg {...props}>
    <path d="m9 18 6-6-6-6" />
  </Svg>
)

/*
 * Pause und Wiedergabe sind gefuellte Zeichen, keine Umrisse.
 *
 * Der gemeinsame Rahmen zeichnet nur Linien (`fill="none"`). Zwei Balken als
 * Umriss werden daraus haarfeine hohle Striche -- bei 16 Pixel in Grau sieht
 * man sie schlicht nicht. Genau so ist es im Betrieb passiert: der Knopf war
 * da und trotzdem unsichtbar.
 */
export const PauseIcon = ({ size = 22, className }: IconProps) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="currentColor"
    className={className}
    aria-hidden="true"
    focusable="false"
  >
    <rect x="7" y="5" width="3.5" height="14" rx="1" />
    <rect x="13.5" y="5" width="3.5" height="14" rx="1" />
  </svg>
)

export const PlayIcon = ({ size = 22, className }: IconProps) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="currentColor"
    className={className}
    aria-hidden="true"
    focusable="false"
  >
    <path d="M8 5.5v13l11-6.5z" />
  </svg>
)

export const ChevronDownIcon = (props: IconProps) => (
  <Svg {...props}>
    <path d="m6 9 6 6 6-6" />
  </Svg>
)

export const ArchiveIcon = (props: IconProps) => (
  <Svg {...props}>
    <rect x="3" y="4" width="18" height="4" rx="1" />
    <path d="M5 8v11a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1V8" />
    <path d="M10 12h4" />
  </Svg>
)

/**
 * Ein Haken und zwei Haken -- gelesen oder noch nicht.
 *
 * Zwei getrennte Zeichen statt eines mit Zustand, weil sie Verschiedenes
 * sagen: **ein** Haken heißt "steht für sie bereit", **zwei** heißt "sie hat
 * es gelesen". Ein "zugestellt" im Sinne eines Messengers gibt es hier nicht
 * und kann es nicht geben: ohne dauerhafte Verbindung weiß der Server nicht,
 * ob ein Gerät die Nachricht schon geholt hat. Der einzelne Haken behauptet
 * deshalb bewusst nur, dass sie abholbereit liegt.
 */
export const CheckIcon = (props: IconProps) => (
  <Svg {...props}>
    <path d="m4 12 5 5L20 6" />
  </Svg>
)

export const DoubleCheckIcon = (props: IconProps) => (
  <Svg {...props}>
    <path d="m2 12 5 5L17 7" />
    <path d="m11 17 1.5 1.5L23 8" />
  </Svg>
)

/**
 * Durchgestrichenes Auge -- steht für eine zurückgezogene Reise.
 *
 * Bewusst ein anderes Bild als der Archivkasten: abgeschlossen heißt "es
 * kommt nichts mehr dazu", zurückgezogen heißt "niemand sieht es mehr außer
 * dir". Zwei Stufen, zwei Zeichen.
 */
export const HiddenIcon = (props: IconProps) => (
  <Svg {...props}>
    <path d="M3 3l18 18" />
    <path d="M10.6 5.2A9.8 9.8 0 0 1 12 5c5 0 9 4.5 9 7a11 11 0 0 1-2.5 3.4" />
    <path d="M6.2 6.9A11.3 11.3 0 0 0 3 12c0 2.5 4 7 9 7a9.6 9.6 0 0 0 4-.9" />
    <path d="M9.9 9.9a3 3 0 0 0 4.2 4.2" />
  </Svg>
)

/**
 * Drei Zeilen -- „hier steht ein Text".
 *
 * Steht in der Spot-Liste hinter dem Namen. Ohne dieses Zeichen sieht eine
 * Zeile mit Text aus wie eine ohne, und niemand tippt darauf.
 */
export const NotizIcon = (props: IconProps) => (
  <Svg {...props}>
    <path d="M4 6h16" />
    <path d="M4 12h16" />
    <path d="M4 18h10" />
  </Svg>
)

export const PencilIcon = (props: IconProps) => (
  <Svg {...props}>
    <path d="M12 20h9" />
    <path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4Z" />
  </Svg>
)

/**
 * A sheet with a folded corner and two lines -- the draft of the report
 * (1.22.0).
 *
 * The draft wore the bare pencil for one version; from the field: "Der
 * Stift deutet auf Änderung hin." A draft is not a change, it is written
 * and not yet sent -- the sheet mail programs show for theirs. The pencil
 * stays where it means "change" (a message, the workbench's pin).
 */
export const EntwurfIcon = (props: IconProps) => (
  <Svg {...props}>
    <path d="M14 3H6a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9z" />
    <path d="M14 3v6h6" />
    <path d="M8 13h8" />
    <path d="M8 17h5" />
  </Svg>
)

/**
 * A pin with a pencil -- "edit the map" (#85, 1.21.0).
 *
 * The bare pencil moved to the draft of the report in 1.21.0; since 1.22.0
 * the draft shows a sheet (`EntwurfIcon`), and the pin with the pencil
 * stays: it says "map" and "change" in one sign, and a pencil is a change.
 */
export const KartenstiftIcon = (props: IconProps) => (
  <Svg {...props}>
    <path d="M9.5 19.5S3 14.5 3 9.5a6.5 6.5 0 0 1 13 0c0 .6-.1 1.2-.3 1.8" />
    <circle cx="9.5" cy="9.5" r="2.2" />
    <path d="M19.2 12.3a1.7 1.7 0 0 1 2.4 2.4L15.5 20.8l-3.2.7.7-3.2Z" />
  </Svg>
)

/** Three lines: a menu to choose from (the workbench of the map, 1.191.0). */
export const MenuIcon = (props: IconProps) => (
  <Svg {...props}>
    <path d="M4 7h16" />
    <path d="M4 12h16" />
    <path d="M4 17h16" />
  </Svg>
)

/**
 * Gebogener Pfeil nach links -- „darauf antworten".
 *
 * Dieselbe Form, die Messenger dafür seit Jahren verwenden: der Pfeil zeigt
 * zurück auf das, worauf man sich bezieht. Ein Sprechblasensymbol wäre hier
 * mehrdeutig, denn alles ringsum ist schon eine Sprechblase.
 */
export const ReplyIcon = (props: IconProps) => (
  <Svg {...props}>
    <path d="M9 17 4 12l5-5" />
    <path d="M20 18v-2a4 4 0 0 0-4-4H4" />
  </Svg>
)

/** Durchgestrichene Glocke -- steht für eine stummgeschaltete Reise. */
export const MuteIcon = (props: IconProps) => (
  <Svg {...props}>
    <path d="M13.7 21a2 2 0 0 1-3.4 0" />
    <path d="M18 8a6 6 0 0 0-9.3-5" />
    <path d="M6.3 6.3A6 6 0 0 0 6 8c0 7-3 9-3 9h14" />
    <path d="m2 2 20 20" />
  </Svg>
)

export const BackIcon = (props: IconProps) => (
  <Svg {...props}>
    <path d="M19 12H5" />
    <path d="m12 19-7-7 7-7" />
  </Svg>
)

/**
 * The trips: a suitcase (1.53.2, from use: "wenn es nur auf die
 * Reiseübersichts-Seite führt: ein Reisesymbol; ein Zurück-Symbol nur, wenn
 * man in einem verschachtelten Untermenü ist"). The link at the top of the
 * chat, the map, the settings and the new trip leads to the list of trips
 * and nowhere "back"; an arrow there promised a step up that is none. The
 * arrow stays where a page lies under another (info, book, photos, search,
 * administration). Not `TascheIcon`: that is the shopping bag of a category.
 */
export const ReisenIcon = (props: IconProps) => (
  <Svg {...props}>
    <rect x="3" y="7" width="18" height="13" rx="2" />
    <path d="M8 7V5a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
    <path d="M8 7v13" />
    <path d="M16 7v13" />
  </Svg>
)

export const SendIcon = (props: IconProps) => (
  <Svg {...props}>
    <path d="M22 2 11 13" />
    <path d="M22 2 15 22l-4-9-9-4Z" />
  </Svg>
)

export const PlusIcon = (props: IconProps) => (
  <Svg {...props}>
    <path d="M12 5v14" />
    <path d="M5 12h14" />
  </Svg>
)

/** Ein Kalenderblatt -- der Tagestrenner (#113) und sein Knopf in der Pille. */
export const KalenderIcon = (props: IconProps) => (
  <Svg {...props}>
    <rect x="3" y="5" width="18" height="16" rx="2" />
    <path d="M3 10h18" />
    <path d="M8 3v4" />
    <path d="M16 3v4" />
  </Svg>
)

export const InfoIcon = (props: IconProps) => (
  <Svg {...props}>
    <circle cx="12" cy="12" r="10" />
    <path d="M12 16v-4" />
    <path d="M12 8h.01" />
  </Svg>
)

/**
 * A funnel (1.93.0, #114): the filter of the chat. Filled while a filter is
 * on -- the same weight change as the marks of reach (`gefuellt`), so that
 * one sees from the header that something is left out.
 */
export const FilterIcon = (props: IconProps & { gefuellt?: boolean }) => (
  <Svg {...props}>
    <path d="M3 4h18l-7 8.5V19l-4 2v-8.5z" />
  </Svg>
)

export const SettingsIcon = (props: IconProps) => (
  <Svg {...props}>
    <circle cx="12" cy="12" r="3" />
    <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 1 1-4 0v-.09a1.65 1.65 0 0 0-1.08-1.51 1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 1 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 1 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 1 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1Z" />
  </Svg>
)

/**
 * Moving posts (#130, 1.117.3): one arrow up, one down -- to another place
 * in the chat, either way.
 */
export const MoveIcon = (props: IconProps) => (
  <Svg {...props}>
    <path d="M8 20V4" />
    <path d="M4 8l4-4 4 4" />
    <path d="M16 4v16" />
    <path d="M12 16l4 4 4-4" />
  </Svg>
)

export const TrashIcon = (props: IconProps) => (
  <Svg {...props}>
    <path d="M3 6h18" />
    <path d="M8 6V4a1 1 0 0 1 1-1h6a1 1 0 0 1 1 1v2" />
    <path d="M19 6v14a1 1 0 0 1-1 1H6a1 1 0 0 1-1-1V6" />
  </Svg>
)

/**
 * Eine Person — **eine** Empfängerin, und damit: privat.
 *
 * Aus dem Betrieb: „bisher wurde das Schloss-Symbol als Kennzeichnung für
 * private Nachrichten verwendet. Das deutet aber auf Verschlüsselung hin."
 * Stimmt — und mit einer echten Ende-zu-Ende-Verschlüsselung (#23) würde es
 * geradezu falsch.
 *
 * **Person und Gruppe sind ein Paar**, und der Unterschied ist die Anzahl.
 * Genau das ist die Aussage: einer sieht es, oder alle. Ein Schloss, ein Auge
 * oder ein Megafon daneben wären drei Bildsprachen für eine Frage.
 */
export const PersonIcon = (props: IconProps) => (
  <Svg {...props} gefuellt>
    <circle cx="12" cy="7.5" r="4" />
    <path d="M12 13.5c-4.4 0-8 2.6-8 5.9V21h16v-1.6c0-3.3-3.6-5.9-8-5.9Z" />
  </Svg>
)

/** Mehrere Personen — an alle, die mitlesen. Das Gegenstück zu `PersonIcon`. */
export const GruppeIcon = (props: IconProps) => (
  <Svg {...props} gefuellt>
    <circle cx="9" cy="8" r="3.6" />
    <path d="M9 13.6c-3.9 0-7 2.3-7 5.2V21h14v-2.2c0-2.9-3.1-5.2-7-5.2Z" />
    <circle cx="17.6" cy="9" r="2.7" />
    <path d="M17.6 13.6c-.9 0-1.7.12-2.5.35 1.5 1.3 2.4 3 2.4 4.85V21H23v-2.2c0-2.9-2.4-5.2-5.4-5.2Z" />
  </Svg>
)

/**
 * Ein Pfeil nach oben — der Sendeknopf im Feld.
 *
 * **Und kein Papierflieger.** Der stand vorher da; Apple nimmt im
 * Eingabefeld einen Pfeil, und genau darum ging es bei #8. Der Flieger
 * bleibt als `SendIcon` stehen, falls ihn woanders jemand braucht.
 */
export const PfeilHochIcon = (props: IconProps) => (
  <Svg {...props}>
    <path d="M12 20V5" />
    <path d="m5 12 7-7 7 7" />
  </Svg>
)

/**
 * Zwei Blätter übereinander — den Wortlaut in die Zwischenablage.
 *
 * Neu mit dem Menü an der Sprechblase (#8): auf iOS unterdrückt
 * `-webkit-touch-callout: none` das Systemmenü, damit das lange Drücken
 * unseres wird -- und damit fiel „Kopieren" weg. Es kommt hier wieder,
 * und ist jetzt auch am Rechner da, wo es das vorher nie gab.
 */
export const KopierenIcon = (props: IconProps) => (
  <Svg {...props}>
    <rect x="9" y="9" width="12" height="12" rx="2" />
    <path d="M5 15V5a2 2 0 0 1 2-2h10" />
  </Svg>
)

/** Eine Stecknadel — der Punkt auf der Karte, an dem etwas war. */
/**
 * A speech bubble -- "this point stands in a post already" in the picker of
 * "Link zur Karte" (1.113.0). A mark, not a button.
 */
export const SprechblaseIcon = (props: IconProps) => (
  <Svg {...props}>
    <path d="M4 5h16v11H9l-5 4V5Z" />
  </Svg>
)

/** A folded map (1.190.0): the tab "Karte" of the new navigation. */
export const MapIcon = (props: IconProps) => (
  <Svg {...props}>
    <path d="M3 6l6-2 6 2 6-2v14l-6 2-6-2-6 2Z" />
    <path d="M9 4v14M15 6v14" />
  </Svg>
)

export const PinIcon = (props: IconProps) => (
  <Svg {...props}>
    <path d="M12 21s7-5.5 7-11a7 7 0 1 0-14 0c0 5.5 7 11 7 11Z" />
    <circle cx="12" cy="10" r="2.5" />
  </Svg>
)

/**
 * Der eigene Standort — das Fadenkreuz, nicht die Nadel.
 *
 * **Es muss sich von `PinIcon` unterscheiden lassen**, denn beide stehen
 * nebeneinander: „Highlight" setzt einen Punkt, wo man hintippt, „Hier" setzt
 * ihn dorthin, wo das Gerät gerade steht. Eine zweite Nadel hieße zweimal
 * dasselbe. Vier Striche nach außen und ein Punkt in der Mitte sind das
 * Zeichen, das jede Kartenanwendung dafür benutzt — es muss niemand lernen.
 */
export const StandortIcon = (props: IconProps) => (
  <Svg {...props}>
    <circle cx="12" cy="12" r="6" />
    <circle cx="12" cy="12" r="1.6" fill="currentColor" stroke="none" />
    <path d="M12 2v3M12 19v3M2 12h3M19 12h3" />
  </Svg>
)

/* --- Wetter ---------------------------------------------------------------
 *
 * Acht Zeichen für die acht Lagen aus `lib/wetterlage.ts`, damit die Kachel im
 * Kartenstapel ohne Worte auskommt. Selbst gezeichnet und nicht aus einem
 * Paket: der Grund oben in dieser Datei gilt hier genauso.
 *
 * **Keine Nacht-Zeichen.** Gespeichert wird nur die Lage, nicht Apples
 * `daylight`; ein Mond ließe sich nur aus dem Zeitstempel raten, und das hieße
 * Sonnenstand rechnen für eine Kachel von Daumengröße.
 */

/** Klar — auch „überwiegend klar". */
export const SonneIcon = (props: IconProps) => (
  <Svg {...props}>
    <circle cx="12" cy="12" r="4.5" />
    <path d="M12 2v2" />
    <path d="M12 20v2" />
    <path d="M2 12h2" />
    <path d="M20 12h2" />
    <path d="m4.9 4.9 1.5 1.5" />
    <path d="m17.6 17.6 1.5 1.5" />
    <path d="m19.1 4.9-1.5 1.5" />
    <path d="m6.4 17.6-1.5 1.5" />
  </Svg>
)

/** Teils bewölkt: die Sonne lugt hinter der Wolke hervor. */
export const TeilsIcon = (props: IconProps) => (
  <Svg {...props}>
    <circle cx="8" cy="8" r="3" />
    <path d="M8 1.5V3" />
    <path d="M1.5 8H3" />
    <path d="m3.4 3.4 1 1" />
    <path d="m12.6 3.4-1 1" />
    <path d="M17.5 20H8a4.5 4.5 0 0 1 0-9 5 5 0 0 1 9.5 1.5 3.75 3.75 0 0 1 0 7.5Z" />
  </Svg>
)

/** Bewölkt. */
export const WolkeIcon = (props: IconProps) => (
  <Svg {...props}>
    <path d="M17.5 19H7a4.5 4.5 0 0 1 0-9 5.5 5.5 0 0 1 10.4 1.5 3.75 3.75 0 0 1 .1 7.5Z" />
  </Svg>
)

/** Regen — Niesel, Regen, starker Regen. */
export const RegenIcon = (props: IconProps) => (
  <Svg {...props}>
    <path d="M17.5 15H7a4.5 4.5 0 0 1 0-9 5.5 5.5 0 0 1 10.4 1.5 3.75 3.75 0 0 1 .1 7.5Z" />
    <path d="M8 18.5 7 21" />
    <path d="M12.5 18.5 11.5 21" />
    <path d="M17 18.5 16 21" />
  </Svg>
)

/** Schnee — und Schneeregen und Hagel. */
export const SchneeIcon = (props: IconProps) => (
  <Svg {...props}>
    <path d="M17.5 15H7a4.5 4.5 0 0 1 0-9 5.5 5.5 0 0 1 10.4 1.5 3.75 3.75 0 0 1 .1 7.5Z" />
    <path d="M8 19h.01" />
    <path d="M12 21h.01" />
    <path d="M16 19h.01" />
    <path d="M10 21.5h.01" />
    <path d="M14 17.5h.01" />
  </Svg>
)

/** Gewitter. */
export const GewitterIcon = (props: IconProps) => (
  <Svg {...props}>
    <path d="M17.5 14H7a4.5 4.5 0 0 1 0-9 5.5 5.5 0 0 1 10.4 1.5 3.75 3.75 0 0 1 .1 7.5Z" />
    <path d="m13 16-3 4h4l-3 4" />
  </Svg>
)

/** Nebel — und Dunst. */
export const NebelIcon = (props: IconProps) => (
  <Svg {...props}>
    <path d="M17.5 13H7a4.5 4.5 0 0 1 0-9 5.5 5.5 0 0 1 10.4 1.5 3.75 3.75 0 0 1 .1 7.5Z" />
    <path d="M5 17h9" />
    <path d="M17 17h2" />
    <path d="M7 21h10" />
  </Svg>
)

/** Wind — windig und frischer Wind. */
export const WindIcon = (props: IconProps) => (
  <Svg {...props}>
    <path d="M3 8h10a3 3 0 1 0-3-3" />
    <path d="M3 12h14a3 3 0 1 1-3 3" />
    <path d="M3 16h7a2.5 2.5 0 1 1-2.5 2.5" />
  </Svg>
)

/* --- Umgebung -------------------------------------------------------------
 *
 * Sieben Zeichen fuer die sieben Schubladen der Umgebungssuche
 * (`server/src/Umgebung.php`). Selbst gezeichnet, aus demselben Grund wie
 * oben -- und hier kommt einer dazu: Apple zeigt in seiner eigenen Liste
 * farbige Symbole, und die gehoeren Apple. Diese hier sind Striche in der
 * Schriftfarbe wie jedes andere Zeichen in dieser Anwendung.
 */

/** Essen & Trinken: Messer und Gabel. */
export const BesteckIcon = (props: IconProps) => (
  <Svg {...props}>
    <path d="M6 3v7a2 2 0 0 0 4 0V3" />
    <path d="M8 10v11" />
    <path d="M17 3c-1.5 1.5-2 3.5-2 5.5S15.5 12 17 12s2-1.5 2-3.5S18.5 4.5 17 3Z" />
    <path d="M17 12v9" />
  </Svg>
)

/** Bar & Abend: ein Glas. */
export const GlasIcon = (props: IconProps) => (
  <Svg {...props}>
    <path d="M5 4h14l-7 8Z" />
    <path d="M12 12v7" />
    <path d="M8 19h8" />
  </Svg>
)

/** Kultur: ein Haus mit Saeulen. */
export const SaeulenIcon = (props: IconProps) => (
  <Svg {...props}>
    <path d="m3 9 9-5 9 5" />
    <path d="M5 9v8" />
    <path d="M10 9v8" />
    <path d="M14 9v8" />
    <path d="M19 9v8" />
    <path d="M3 20h18" />
  </Svg>
)

/** Sehenswuerdigkeit: ein Stern -- wie in Apples eigener Liste. */
export const SternIcon = (props: IconProps) => (
  <Svg {...props}>
    <path d="m12 3 2.6 5.6 6.4.8-4.7 4.3 1.2 6.3-5.5-3-5.5 3 1.2-6.3L3 9.4l6.4-.8Z" />
  </Svg>
)

/** Natur & Aussicht: ein Berg. */
export const BergIcon = (props: IconProps) => (
  <Svg {...props}>
    <path d="m2 19 6.5-11 4 6.5 2.5-4L21 19Z" />
    <path d="m6.5 11.5 2 1.5" />
  </Svg>
)

/** Musik (#102): eine Note mit zwei Koepfen. */
export const NoteIcon = (props: IconProps) => (
  <Svg {...props}>
    <path d="M9 18V5l12-2v13" />
    <circle cx="6" cy="18" r="3" />
    <circle cx="18" cy="16" r="3" />
  </Svg>
)

/** Uebernachtung: ein Bett. */
export const BettIcon = (props: IconProps) => (
  <Svg {...props}>
    <path d="M3 18v-9" />
    <path d="M3 13h18v5" />
    <path d="M21 18v-4a3 3 0 0 0-3-3h-7v2" />
    <circle cx="7" cy="10.5" r="1.5" />
  </Svg>
)

/** Markt & Laden: eine Einkaufstasche. */
export const TascheIcon = (props: IconProps) => (
  <Svg {...props}>
    <path d="M5 8h14l-1 12H6Z" />
    <path d="M9 11V7a3 3 0 0 1 6 0v4" />
  </Svg>
)

/** Camping & Stellplatz: ein Zelt. */
export const ZeltIcon = (props: IconProps) => (
  <Svg {...props}>
    <path d="M12 4 3 20h18Z" />
    <path d="M12 4v16" />
    <path d="m8.5 20 3.5-6 3.5 6" />
  </Svg>
)

/* --- Symbole der Einstellungskategorien ---------------------------------- */
/*
 * Sie stehen vor jeder Zeile der Kategorienleiste (`Kategorien.tsx`). Aus dem
 * Betrieb: „vor jeder Einstellungskategorie sollte ein passendes Symbol
 * stehen." Strichsymbole wie alle anderen hier -- eine zweite Farbwelt aus
 * bunten Kacheln stünde quer zu einer Anwendung, deren Farben einstellbar
 * sind.
 *
 * **Hier stehen nur die, die es noch nicht gab.** Für Konto, Empfänger,
 * Benachrichtigung und Ort nimmt die Leiste `PersonIcon`, `GruppeIcon`,
 * `GlockeIcon` und `StandortIcon` -- ein zweites Zeichen für dieselbe Sache
 * wäre genau das, wovor der Doc-Block an `PersonIcon` warnt.
 */

/** Jemand möchte dazu: eine Person mit Pluszeichen. */
export const PersonPlusIcon = (props: IconProps) => (
  <Svg {...props}>
    <circle cx="9" cy="8" r="3.5" />
    <path d="M2.5 20a6.5 6.5 0 0 1 13 0" />
    <path d="M18 8v6" />
    <path d="M15 11h6" />
  </Svg>
)

/** Darstellung: zwei Buchstaben in zwei Größen. */
export const TextSizeIcon = (props: IconProps) => (
  <Svg {...props}>
    <path d="M2 19 7.5 5l5.5 14" />
    <path d="M4 14h7" />
    <path d="M15 19l3.5-8 3.5 8" />
    <path d="M16.2 16h4.6" />
  </Svg>
)

/** Diagnose: eine Herzschlaglinie -- Messwerte, keine Bedienung. */
export const PulseIcon = (props: IconProps) => (
  <Svg {...props}>
    <path d="M2 12h4l2.5-6 4 13L15 12h7" />
  </Svg>
)

/**
 * Sichern: ein Pfeil in eine Ablage.
 *
 * Nicht das iOS-Teilen-Zeichen (Kasten mit Pfeil nach oben): der Knopf sichert
 * ein Bild, und dass das Gerät dafuer sein Teilen-Blatt aufmacht, ist der Weg
 * und nicht die Sache. Auf Android und am Rechner waere es ausserdem falsch.
 */
export const SichernIcon = (props: IconProps) => (
  <Svg {...props}>
    <path d="M12 3v11" />
    <path d="M7.5 10 12 14.5 16.5 10" />
    <path d="M4.5 19.5h15" />
  </Svg>
)

/**
 * Datenschutz: ein Schild.
 *
 * Kein Schloss -- das steht in dieser Anwendung schon fuer „sieht nur einer"
 * an den Nachrichten, und zweimal dasselbe Zeichen fuer zwei Dinge waere die
 * Sorte Verwechslung, die man erst im Betrieb bemerkt.
 */
export const SchildIcon = (props: IconProps) => (
  <Svg {...props}>
    <path d="M12 3l7.5 3v5.5c0 4.4-3 8.1-7.5 9.5-4.5-1.4-7.5-5.1-7.5-9.5V6z" />
  </Svg>
)

/**
 * Open source: two hands passing something on.
 *
 * Not a padlock and not a scroll -- the first says "protected", the second
 * says "law". What this category is about is neither: it is a list of work
 * that other people gave away.
 */
export const OpenSourceIcon = (props: IconProps) => (
  <Svg {...props}>
    <path d="M12 3a9 9 0 0 0-3 17.5V17a3 3 0 0 1 6 0v3.5A9 9 0 0 0 12 3z" />
    <path d="M9 10.5h6" />
  </Svg>
)

/** Administration: Schieberegler -- Stellschrauben der ganzen Installation. */
export const SlidersIcon = (props: IconProps) => (
  <Svg {...props}>
    <path d="M4 6h10" />
    <path d="M18 6h2" />
    <circle cx="16" cy="6" r="2" />
    <path d="M4 18h4" />
    <path d="M12 18h8" />
    <circle cx="10" cy="18" r="2" />
  </Svg>
)

/** Speicherplatz: ein gestapelter Datenträger. */
export const DiskIcon = (props: IconProps) => (
  <Svg {...props}>
    <ellipse cx="12" cy="6" rx="8" ry="3" />
    <path d="M4 6v6c0 1.7 3.6 3 8 3s8-1.3 8-3V6" />
    <path d="M4 12v6c0 1.7 3.6 3 8 3s8-1.3 8-3v-6" />
  </Svg>
)

/** Farben: eine Palette mit einem Tropfen. */
export const PaletteIcon = (props: IconProps) => (
  <Svg {...props}>
    <path d="M12 3a9 9 0 1 0 0 18c1.1 0 2-.9 2-2 0-1.4-1-1.7-1-2.6 0-.8.7-1.4 1.5-1.4H17a4 4 0 0 0 4-4c0-4.4-4-8-9-8Z" />
    <circle cx="8" cy="10" r="1" />
    <circle cx="12" cy="7.5" r="1" />
    <circle cx="16" cy="10" r="1" />
  </Svg>
)

/** Einladen: ein Kettenglied -- der Link, den jemand bekommt. */
export const LinkIcon = (props: IconProps) => (
  <Svg {...props}>
    <path d="M10 13.5a4 4 0 0 0 5.7 0l3-3a4 4 0 1 0-5.7-5.7l-1.6 1.6" />
    <path d="M14 10.5a4 4 0 0 0-5.7 0l-3 3a4 4 0 1 0 5.7 5.7l1.6-1.6" />
  </Svg>
)

/** Zurücksetzen: ein Pfeil, der im Kreis zurückläuft. */
export const ResetIcon = (props: IconProps) => (
  <Svg {...props}>
    <path d="M4 11a8 8 0 1 1 2.3 5.7" />
    <path d="M4 5v6h6" />
  </Svg>
)

/** Hinausgehen: eine Tür mit einem Pfeil nach draußen. */
/** Das Buch (#14) — der Reisebericht zum Drucken. */
export const BuchIcon = (props: IconProps) => (
  <Svg {...props}>
    <path d="M4 4.5A1.5 1.5 0 0 1 5.5 3H19v16H5.5A1.5 1.5 0 0 0 4 20.5Z" />
    <path d="M4 20.5A1.5 1.5 0 0 1 5.5 19H19" />
    <path d="M8 7h7" />
    <path d="M8 11h7" />
  </Svg>
)

export const ExitIcon = (props: IconProps) => (
  <Svg {...props}>
    <path d="M14 4H6a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h8" />
    <path d="M17 8l4 4-4 4" />
    <path d="M21 12H10" />
  </Svg>
)
